/**
 * Registration Pending Page
 * Shown to manufacturers after they submit registration for admin approval
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registrationService } from '../services/registration.service';

export default function RegistrationPendingPage() {
  const navigate = useNavigate();
  const [registrationStatus, setRegistrationStatus] = useState<string>('under_review');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkRegistrationStatus();
  }, []);

  const checkRegistrationStatus = async () => {
    try {
      const session = await registrationService.getRegistrationSession();
      setRegistrationStatus(session.status);
      
      // If approved, redirect to dashboard
      if (session.status === 'APPROVED') {
        navigate('/manufacturer');
      }
    } catch (error) {
      console.error('Error checking registration status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatus = () => {
    setLoading(true);
    checkRegistrationStatus();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking registration status...</p>
        </div>
      </div>
    );
  }

  const getStatusInfo = () => {
    switch (registrationStatus) {
      case 'UNDER_REVIEW':
        return {
          icon: '⏳',
          title: 'Registration Under Review',
          message: 'Your registration is currently being reviewed by our admin team.',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200'
        };
      case 'REJECTED':
        return {
          icon: '❌',
          title: 'Registration Rejected',
          message: 'Your registration has been rejected. Please contact support for more information.',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      default:
        return {
          icon: '📋',
          title: 'Registration Submitted',
          message: 'Your registration has been submitted and is awaiting review.',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Registration Status</h1>
        <p className="text-sm text-gray-600 mt-1">
          Track your manufacturer registration progress
        </p>
      </div>

      {/* Main Content */}
      <section>
        <div className="bg-white rounded-lg shadow p-8">
          <div className={`${statusInfo.bgColor} ${statusInfo.borderColor} border rounded-lg p-6 mb-6`}>
            <div className="flex items-center">
              <div className="text-4xl mr-4">{statusInfo.icon}</div>
              <div>
                <h2 className={`text-xl font-semibold ${statusInfo.color}`}>
                  {statusInfo.title}
                </h2>
                <p className="text-gray-700 mt-2">
                  {statusInfo.message}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">What happens next?</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-xs font-medium text-blue-600">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Admin Review</p>
                    <p className="text-sm text-gray-600">Our team will review your registration details and documents.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-xs font-medium text-blue-600">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email Notification</p>
                    <p className="text-sm text-gray-600">You'll receive an email once your registration is approved or if additional information is needed.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-xs font-medium text-blue-600">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Access Dashboard</p>
                    <p className="text-sm text-gray-600">Once approved, you'll be able to access your manufacturer dashboard and start using the platform.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Need Help?</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">
                  If you have questions about your registration or need to update any information:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Email us at support@pureaum.com</li>
                  <li>• Call us at +91-XXXX-XXXX-XX</li>
                  <li>• Review typically takes 1-3 business days</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-between items-center pt-6 border-t">
              <button
                onClick={handleRefreshStatus}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50"
              >
                {loading ? 'Checking...' : 'Refresh Status'}
              </button>
              
              {registrationStatus === 'REJECTED' && (
                <button
                  onClick={() => navigate('/enhanced-registration')}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Update Registration
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
